import React, { useState, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import useClaimLimit from './hooks/useClaimLimit';
import ClaimLimitTable from './components/ClaimLimitTable';
import ClaimLimitDialog from './components/ClaimLimitDialog';
import GenericTableFilter from '../../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../../contexts/LanguageContext';

const WarrantyClaimLimitPage = () => {
    const { th } = useLanguage();
    const toast = React.useRef(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedClaimLimit, setSelectedClaimLimit] = useState(null);
    const [isEdit, setIsEdit] = useState(false);

    const { claimLimits, totalCount, loading, lazyParams, onPage, onSort, handleCreate, handleUpdate, handleDelete, refetch, activeFilters, handleFilterChange, initFilters, productGroups } = useClaimLimit();

    const filterFields = useMemo(
        () => [
            { field: 'productGroup', headerName: th('warrantyClaimLimit.filterFields.productGroup'), type: 'select', options: productGroups },
            { field: 'responsibleName', headerName: th('warrantyClaimLimit.filterFields.responsibleName'), type: 'text' },
            { field: 'startDate', headerName: th('warrantyClaimLimit.filterFields.startDate'), type: 'date' },
            { field: 'endDate', headerName: th('warrantyClaimLimit.filterFields.endDate'), type: 'date' }
        ],
        [productGroups, th]
    );

    const customFilterTypes = useMemo(() => ({}), []);

    const handleSearch = (searchValue) => {
        // Global search için kullanılabilir, şimdilik boş bırakıyoruz
    };

    const handleCreateClick = () => {
        setIsEdit(false);
        setSelectedClaimLimit(null);
        setDialogVisible(true);
    };

    const handleEditClick = (claimLimit) => {
        setIsEdit(true);
        setSelectedClaimLimit(claimLimit);
        setDialogVisible(true);
    };

    const handleDeleteClick = (id) => {
        confirmDialog({
            message: th('warrantyClaimLimit.confirmDialog.message'),
            header: th('warrantyClaimLimit.confirmDialog.header'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: async () => {
                const result = await handleDelete(id);
                if (result?.isSuccessful) {
                    toast.current.show({
                        severity: 'success',
                        summary: th('warrantyClaimLimit.toastMessages.successSummary'),
                        detail: th('warrantyClaimLimit.toastMessages.deleteSuccess'),
                        life: 3000
                    });
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: th('warrantyClaimLimit.toastMessages.errorSummary'),
                        detail: th('warrantyClaimLimit.toastMessages.deleteError'),
                        life: 3000
                    });
                }
            },
            rejectClassName: 'p-button-outlined'
        });
    };

    const handleSubmit = async (formData) => {
        const result = isEdit ? await handleUpdate(formData) : await handleCreate(formData);
        if (result?.isSuccessful) {
            toast.current.show({
                severity: 'success',
                summary: th('warrantyClaimLimit.toastMessages.successSummary'),
                detail: isEdit ? th('warrantyClaimLimit.toastMessages.updateSuccess') : th('warrantyClaimLimit.toastMessages.createSuccess'),
                life: 3000
            });
            setDialogVisible(false);
            refetch();
        } else {
            toast.current.show({
                severity: 'error',
                summary: th('warrantyClaimLimit.toastMessages.errorSummary'),
                detail: isEdit ? th('warrantyClaimLimit.toastMessages.updateError') : th('warrantyClaimLimit.toastMessages.createError'),
                life: 3000
            });
        }
        return result;
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between mb-4">
                <h1 className="m-0">{th('warrantyClaimLimit.title')}</h1>
                <Button label={th('warrantyClaimLimit.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />
            </div>

            <div className="mb-2">
                <GenericTableFilter
                    filterFields={filterFields}
                    initialFilters={{ productGroup: '', responsibleName: '', startDate: null, endDate: null }}
                    onSearch={handleSearch}
                    onApplyFilters={handleFilterChange}
                    onResetFilters={initFilters}
                    customFilterTypes={customFilterTypes}
                />
            </div>

            <ClaimLimitTable claimLimits={claimLimits} loading={loading} totalCount={totalCount} lazyParams={lazyParams} onPage={onPage} onSort={onSort} onEdit={handleEditClick} onDelete={handleDeleteClick} />

            <ClaimLimitDialog visible={dialogVisible} onHide={() => setDialogVisible(false)} onSubmit={handleSubmit} initialData={selectedClaimLimit} isEdit={isEdit} loading={loading} />
        </div>
    );
};

export default WarrantyClaimLimitPage;
